import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import { useI18n } from '@/hooks/useI18n';

interface PaymentUpdateDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (amount: number) => Promise<void>;
  totalAmount: number;
  currentPaidAmount: number;
  isLoading?: boolean;
  error?: string | null;
}

const PaymentUpdateDialog: React.FC<PaymentUpdateDialogProps> = ({
  open,
  onClose,
  onConfirm,
  totalAmount,
  currentPaidAmount,
  isLoading = false,
  error = null
}) => {
  const { t } = useI18n();
  const [paymentAmount, setPaymentAmount] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setPaymentAmount(currentPaidAmount.toString());
    }
  }, [open, currentPaidAmount]);

  const handleConfirm = async () => {
    const amount = parseFloat(paymentAmount) || 0;
    await onConfirm(amount);
  };

  const remainingAmount = totalAmount - currentPaidAmount;
  const newPaymentAmount = parseFloat(paymentAmount) || 0;
  const isValidAmount = newPaymentAmount >= 0 && newPaymentAmount <= totalAmount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('bill.persistent.updatePayment')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bill.persistent.totalAmount')}: €{totalAmount.toFixed(2)}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('bill.persistent.remainingAmount')}: €{remainingAmount.toFixed(2)}
          </Typography>

          <TextField
            fullWidth
            type="number"
            label={t('bill.persistent.paidAmount')}
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            disabled={isLoading}
            error={!isValidAmount}
            helperText={!isValidAmount ? t('bill.persistent.invalidAmount') : ''}
            inputProps={{
              min: 0,
              max: totalAmount,
              step: 0.01
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading || !isValidAmount || newPaymentAmount === currentPaidAmount}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {t('common.update')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentUpdateDialog;